import { AlertTriangle, Mail } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { useAuth } from '@/auth/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { usePartnerBookingRequests } from '@/hooks/useBookingRequests';
import { slotTimeRangeLabel } from '@/types/types_availabilitySlot';
import {
  bookingPriceLines,
  bookingPriceTotal,
  bookingRequestEndReasonLabels,
  cancellationOutcomeLabel,
  isBookingNonRefundable,
  isCancellationChargeable,
  bookingFreeCancellationPhrase,
  offerCancellation,
  offerCancellationLabel,
  optionalServiceLines,
  type BookingRequestStatusType,
  type BookingRequestType,
} from '@/types/types_bookingRequest';
import { commissionFor } from '@/types/types_invoice';
import { answerOwnBookingOffer, callableErrorMessage, cancelOwnBooking } from '@/util/callable_functions';
import {
  formatCalendarDate,
  formatDate,
  formatDeadlineDayJst,
  formatDeadlineJst,
  formatJpy,
} from '@/util/util_format';

/**
 * How this page renders a free-cancellation deadline: as the day it runs to,
 * except for a booking whose deadline falls at some hour of a day rather than at
 * the end of one. `bookingFreeCancellationPhrase` picks.
 */
const appDeadlineFormats = { day: formatDeadlineDayJst, instant: formatDeadlineJst };

// How a status reads to the partner that asked, which is not how it reads to us.
// 'unavailable' is a queue outcome in the CMS; here it is the answer to a
// question somebody's guest asked, and it has to say so in words a concierge can
// forward. Kept local to this page for the same reason `statusPillClass` is
// local to the admin list: it is this screen's wording, not the model's.
const partnerStatusLabels: Record<BookingRequestStatusType, string> = {
  pending: 'With Eighty Days',
  held: 'Awaiting your answer',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
  withdrawn: 'Withdrawn',
  expired: 'Hold expired',
  unavailable: 'Not available',
};

const partnerStatusNotes: Partial<Record<BookingRequestStatusType, string>> = {
  pending: 'We are checking your dates with the artisan and will come back to you.',
  expired: 'The date was released when the deadline passed. Contact us if your guest would still like it.',
  unavailable: 'We were not able to secure this experience on the dates you asked for.',
};

const statusPillClass: Record<BookingRequestStatusType, string> = {
  pending: 'tw:bg-amber-100 tw:text-amber-900',
  held: 'tw:bg-ed-gold/25 tw:text-ed-black',
  confirmed: 'tw:bg-green-100 tw:text-green-800',
  cancelled: 'tw:bg-red-100 tw:text-red-800',
  withdrawn: 'tw:bg-gray-100 tw:text-gray-700',
  expired: 'tw:bg-gray-100 tw:text-gray-700',
  unavailable: 'tw:bg-gray-100 tw:text-gray-700',
};

/**
 * Which block of the page a request belongs in.
 *
 * Grouped by what the partner has to do about it rather than by status, because
 * the two do not line up: 'held' is the only one that needs them, and burying it
 * in a list ordered by date is how a deadline gets missed.
 */
type GroupKeyType = 'answer' | 'open' | 'confirmed' | 'closed';

function groupOf(status: BookingRequestStatusType): GroupKeyType {
  switch (status) {
    case 'held':
      return 'answer';
    case 'pending':
      return 'open';
    case 'confirmed':
      return 'confirmed';
    default:
      return 'closed';
  }
}

const groups: { key: GroupKeyType; title: string; blurb?: string }[] = [
  {
    key: 'answer',
    title: 'Needs your answer',
    blurb: 'A date is being held for you. Answer it here, or from the link in the email we sent — '
      + 'either way it is the same answer, and whichever comes first is the one that counts.',
  },
  { key: 'open', title: 'With us' },
  { key: 'confirmed', title: 'Confirmed' },
  { key: 'closed', title: 'Closed' },
];

/**
 * A partner's own booking requests.
 *
 * Mostly a record, with two things a partner may actually do to its own bookings,
 * and they are worth reading as a pair. It may **answer a held offer**, and it may
 * **cancel a confirmed booking** — the second being the larger commitment of the
 * two, since it can carry a fee. Both go through their own callable rather than a
 * relaxation of the admin one: what a partner may decide about its own booking is a
 * different question from what Eighty Days may decide about anyone's.
 *
 * Answering here does not replace the emailed link. The link is what makes an
 * offer answerable by a concierge on a phone at midnight who is not signed in;
 * this is what makes one answerable by the colleague who is signed in and looking
 * at it — and what makes one answerable at all when the offer email bounced. The
 * server takes whichever arrives first and refuses the second.
 */
export function PagePartnerRequests() {
  const { partnerUser } = useAuth();
  const partnerId = partnerUser?.partnerId;
  const { requests, loading, error } = usePartnerBookingRequests(partnerId ?? '');
  /** The booking the cancellation dialog is open for, if any. */
  const [cancelling, setCancelling] = useState<BookingRequestType | null>(null);
  /** The offer being answered, and which way. Both answers are confirmed first. */
  const [answering, setAnswering] = useState<{ request: BookingRequestType; answer: 'confirm' | 'decline' } | null>(
    null,
  );

  if (!partnerId) {
    return (
      <div className='tw:py-16 tw:text-center'>
        <p className='tw:text-lg'>We couldn&rsquo;t find your partner record.</p>
        <p className='tw:mt-2 tw:text-sm tw:text-muted-foreground'>Please contact Eighty Days.</p>
      </div>
    );
  }

  if (loading) {
    return <div className='tw:py-16 tw:text-center tw:text-muted-foreground'>Loading…</div>;
  }

  return (
    // Supplies its own padding: this page sits in the public top-nav layout,
    // which leaves spacing to the page.
    <div className='tw:mx-auto tw:max-w-4xl tw:px-6 tw:py-8'>
      <header className='tw:mb-8'>
        <h1 className='tw:font-serif tw:text-3xl tw:font-light'>Booking requests</h1>
        <p className='tw:mt-1 tw:text-sm tw:text-muted-foreground'>
          Every experience your organisation has asked us for, newest first. To change a date, the number of guests
          or a confirmed booking, please contact Eighty Days.
        </p>
      </header>

      {error && (
        <p className='tw:mb-6 tw:rounded tw:border tw:border-ed-red/30 tw:bg-ed-red/5 tw:px-4 tw:py-2.5 tw:text-sm tw:text-ed-red'>
          {error}
        </p>
      )}

      {requests.length === 0 && !error && (
        <div className='tw:rounded-lg tw:border tw:bg-card tw:px-6 tw:py-12 tw:text-center'>
          <p className='tw:text-sm tw:text-muted-foreground'>
            You haven&rsquo;t requested anything yet.
          </p>
          <Link
            to='/catalog'
            className='tw:mt-6 tw:inline-block tw:bg-ed-gold tw:px-6 tw:py-3 tw:text-xs tw:tracking-widest tw:text-ed-black tw:uppercase tw:transition-colors tw:hover:bg-ed-black tw:hover:text-ed-cream'
          >
            Browse experiences
          </Link>
        </div>
      )}

      <div className='tw:space-y-10'>
        {groups.map(({ key, title, blurb }) => {
          // Already newest-first from the query, so filtering preserves it.
          const inGroup = requests.filter((r) => groupOf(r.status) === key);
          if (inGroup.length === 0) return null;
          return (
            <section key={key}>
              <h2 className='tw:text-sm tw:font-semibold tw:tracking-wide tw:text-muted-foreground tw:uppercase'>
                {title} <span className='tw:font-normal tw:text-ed-stone'>({inGroup.length})</span>
              </h2>
              {blurb && <p className='tw:mt-1.5 tw:text-sm tw:text-muted-foreground'>{blurb}</p>}
              <div className='tw:mt-4 tw:space-y-4'>
                {inGroup.map((request) => (
                  <RequestCard
                    key={request.id}
                    request={request}
                    onCancel={() => setCancelling(request)}
                    onAnswer={(answer) => setAnswering({ request, answer })}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {/* Keyed on the booking, so opening the dialog for a second one starts from
          a clean acceptance rather than inheriting the last one's ticked box. */}
      {cancelling && (
        <CancelBookingDialog
          key={cancelling.id}
          request={cancelling}
          onClose={() => setCancelling(null)}
        />
      )}

      {answering && (
        <AnswerOfferDialog
          key={`${answering.request.id}-${answering.answer}`}
          request={answering.request}
          answer={answering.answer}
          onClose={() => setAnswering(null)}
        />
      )}
    </div>
  );
}

/**
 * Answering a held offer, with the terms restated before anything is pressed.
 *
 * Both answers get a dialog, for different reasons. Confirming commits the partner
 * to a price and starts a cancellation window that is the expensive thing to have
 * skimmed past — so it is quoted here, from the same helper the offer email and
 * the offer page use, rather than left to the row above. Declining releases the
 * date to whoever wants it next and cannot be taken back, which is worth one
 * press to be sure of.
 *
 * A rejection from the server is shown as an ordinary message: the usual cause is
 * that the offer lapsed or a colleague answered it first, and the message says
 * which. The list is live, so the row moves on its own once it has.
 */
function AnswerOfferDialog(
  { request, answer, onClose }: { request: BookingRequestType; answer: 'confirm' | 'decline'; onClose: () => void },
) {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Read once, when the dialog opens, for the same reason the cancellation dialog
  // does it: a window that closes while somebody is reading must not move the
  // screen under them. The server has the last word either way.
  const [openedAt] = useState(() => new Date().toISOString());

  const confirming = answer === 'confirm';
  const cancellation = offerCancellationLabel(offerCancellation(request, openedAt), formatDeadlineDayJst);

  async function submit() {
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      await answerOwnBookingOffer({ requestId: request.id, answer });
      // The snapshot listener moves the card into Confirmed or Closed.
      onClose();
    } catch (e) {
      console.error('Answering the offer failed', e);
      setSaveError(callableErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && !saving && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{confirming ? 'Confirm this booking' : 'Decline this date'}</DialogTitle>
          <DialogDescription>
            {request.experienceName} for {request.customerFirstName} {request.customerLastName} on{' '}
            {formatCalendarDate(request.confirmedDate)}.
          </DialogDescription>
        </DialogHeader>

        {confirming
          ? (
            <div className='tw:space-y-3 tw:text-sm'>
              <p>
                This commits the booking at{' '}
                <strong>{typeof request.agreedPrice === 'number' ? formatJpy(request.agreedPrice) : 'the price offered'}</strong>
                {' '}for {request.guests} {request.guests === 1 ? 'guest' : 'guests'}. We will tell the artisan and
                email you a confirmation.
              </p>
              {cancellation && (
                <p className='tw:text-muted-foreground'>
                  <span className='tw:text-ed-stone'>Cancellation terms:</span> {cancellation}
                </p>
              )}
              <p className='tw:text-muted-foreground'>
                To change the date or the number of guests, please contact Eighty Days rather than confirming here.
              </p>
            </div>
          )
          : (
            <p className='tw:text-sm tw:text-muted-foreground'>
              The date will be released and this request closed. Contact Eighty Days if your guest would like us to
              look at other dates.
            </p>
          )}

        {saveError && <p className='tw:text-sm tw:text-ed-red'>{saveError}</p>}

        <DialogFooter>
          <Button variant='outline' disabled={saving} onClick={onClose}>
            Go back
          </Button>
          <Button
            variant={confirming ? 'default' : 'destructive'}
            loading={saving}
            onClick={() => void submit()}
          >
            {confirming
              ? saving ? 'Confirming…' : 'Confirm booking'
              : saving ? 'Declining…' : 'Decline date'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Cancelling a confirmed booking, and stating what it costs first.
 *
 * The fee is computed here from the same window the server judges it against, and
 * shown before anything is pressed — the terms were agreed at confirmation, and
 * this is the moment they cost money. Where a fee applies it has to be accepted
 * explicitly: a concierge who did not notice it would be the one explaining it to
 * their finance office.
 *
 * The screen is not what decides, though. `acceptedFee` goes with the request and
 * the server refuses anything short of what is due, so a page left open across the
 * deadline is turned back with the real figure rather than honoured at the ¥0 it
 * was still displaying. That refusal is shown as an ordinary message and the
 * recomputed fee appears with it, because trying again is the right next step.
 */
function CancelBookingDialog({ request, onClose }: { request: BookingRequestType; onClose: () => void }) {
  const [accepted, setAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Read once, when the dialog opens: a fee that changed while somebody was reading
  // the screen must not change the screen under them. The server has the last word.
  const [openedAt] = useState(() => new Date().toISOString());

  const chargeable = isCancellationChargeable('client', {
    now: openedAt,
    freeCancellationUntil: request.freeCancellationUntil,
  });
  const fee = chargeable ? request.agreedPrice ?? 0 : 0;
  const freeUntil = bookingFreeCancellationPhrase(request, appDeadlineFormats);

  async function submit() {
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      await cancelOwnBooking({ requestId: request.id, acceptedFee: fee });
      // The snapshot listener moves the card into Closed.
      onClose();
    } catch (e) {
      console.error('Cancelling the booking failed', e);
      setSaveError(callableErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && !saving && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cancel this booking</DialogTitle>
          <DialogDescription>
            {request.experienceName} for {request.customerFirstName} {request.customerLastName} on{' '}
            {formatCalendarDate(request.confirmedDate)}.
          </DialogDescription>
        </DialogHeader>

        {fee > 0 ? (
          <div className='tw:space-y-4'>
            <p className='tw:border tw:border-ed-red/30 tw:bg-ed-red/5 tw:px-3 tw:py-2.5 tw:text-sm'>
              <strong>A cancellation fee of {formatJpy(fee)} applies.</strong>{' '}
              {isBookingNonRefundable(request)
                ? 'This booking was confirmed with the artisan on the day and carried no free cancellation period.'
                : freeUntil
                  ? `Free cancellation ran until ${freeUntil}.`
                  : ''}{' '}
              It will appear on your next monthly invoice.
            </p>
            <label className='tw:flex tw:items-start tw:gap-3 tw:text-sm'>
              <input
                type='checkbox'
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className='tw:mt-0.5 tw:size-4 tw:accent-ed-gold'
              />
              <span>I accept the {formatJpy(fee)} cancellation fee on behalf of my organisation.</span>
            </label>
          </div>
        ) : (
          <p className='tw:text-sm tw:text-muted-foreground'>
            No cancellation fee applies
            {freeUntil && ` — free cancellation runs until ${freeUntil}`}
            . We will tell the artisan and email you a confirmation.
          </p>
        )}

        {saveError && <p className='tw:text-sm tw:text-ed-red'>{saveError}</p>}

        <DialogFooter>
          <Button variant='outline' disabled={saving} onClick={onClose}>
            Keep the booking
          </Button>
          <Button
            variant='destructive'
            disabled={fee > 0 && !accepted}
            loading={saving}
            onClick={() => void submit()}
          >
            {saving ? 'Cancelling…' : 'Cancel booking'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * What the booking costs, as its own table.
 *
 * Out of the fact grid on purpose. That grid is two columns of unrelated facts
 * flowed in reading order, which put an experience's price on the left, a
 * transfer beside it on the right and the total back on the left again —
 * money laid out as if none of it added up. Here every amount is one
 * right-aligned column under the one above it, in the order the offer email
 * listed them and the invoice will show them, so a partner can check the total by
 * eye instead of taking it on trust.
 *
 * The rows are named for what they are: the experience the partner asked for, then
 * whatever was arranged around it. 'Total' appears only where there is arithmetic
 * to show — on a booking that is just its experience it would restate the row
 * above it.
 */
function PriceTable({ request }: { request: BookingRequestType }) {
  const lines = bookingPriceLines(request);
  if (lines.length === 0) return null;

  const total = request.agreedPrice ?? bookingPriceTotal(lines);

  // The rate frozen onto the booking when it was confirmed, never the
  // experience's live one: a rate we have no record of agreeing on this booking
  // is not one to quote a partner. So a held offer shows no commission — it is
  // settled at confirmation, not before — and a confirmed booking shows what it
  // will actually be credited.
  //
  // Earned row by row, because the extras we arrange around a date carry none:
  // commission on a transfer would be paying the partner for placing a taxi. Same
  // sum, from the same helper, as the deduction on the monthly invoice.
  const commission = request.commission?.enabled ? request.commission : null;
  const commissionAmount = commission
    ? lines.reduce((sum, line) => sum + (line.commissionable ? commissionFor(line.amount, commission.percent) : 0), 0)
    : 0;

  return (
    <table className='tw:mt-4 tw:w-full tw:text-sm'>
      <caption className='tw:pb-1 tw:text-left tw:text-[0.65rem] tw:tracking-wider tw:text-ed-stone tw:uppercase'>
        Price
      </caption>
      <tbody>
        {/* Keyed by position: two services on one booking may share a name. */}
        {lines.map((line, i) => (
          <tr key={i} className='tw:border-b tw:border-ed-umber/10'>
            <th scope='row' className='tw:py-1.5 tw:pr-4 tw:text-left tw:font-normal'>
              {i === 0 ? 'Experience price' : line.label}
            </th>
            <td className='tw:py-1.5 tw:text-right tw:tabular-nums'>{formatJpy(line.amount)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        {lines.length > 1 && (
          <tr className='tw:border-b tw:border-ed-umber/10'>
            <th scope='row' className='tw:py-1.5 tw:pr-4 tw:text-left tw:font-medium'>Total</th>
            <td className='tw:py-1.5 tw:text-right tw:font-medium tw:tabular-nums'>{formatJpy(total)}</td>
          </tr>
        )}
        {commissionAmount > 0 && (
          <>
            <tr className='tw:border-b tw:border-ed-umber/10'>
              <th scope='row' className='tw:py-1.5 tw:pr-4 tw:text-left tw:font-normal tw:text-ed-umber'>
                Your commission ({commission?.percent}%)
              </th>
              <td className='tw:py-1.5 tw:text-right tw:tabular-nums tw:text-ed-umber'>
                −{formatJpy(commissionAmount)}
              </td>
            </tr>
            {/* The figure the partner is actually asked for, since the commission is
                taken off the invoice rather than paid across separately. */}
            <tr>
              <th scope='row' className='tw:py-1.5 tw:pr-4 tw:text-left tw:font-medium'>Invoiced to you</th>
              <td className='tw:py-1.5 tw:text-right tw:font-medium tw:tabular-nums'>
                {formatJpy(total - commissionAmount)}
              </td>
            </tr>
          </>
        )}
      </tfoot>
    </table>
  );
}

/**
 * One request. The facts shown vary by status on purpose — a pending request has
 * preferred dates and no price, a confirmed one has a booked date and a
 * cancellation window — and listing the absent ones as dashes would read as
 * missing information rather than information that does not exist yet.
 */
function RequestCard(
  { request, onCancel, onAnswer }: {
    request: BookingRequestType;
    onCancel: () => void;
    onAnswer: (answer: 'confirm' | 'decline') => void;
  },
) {
  const rows: [string, string][] = [];

  if (request.confirmedDate) {
    rows.push([request.status === 'confirmed' ? 'Date' : 'Date offered', formatCalendarDate(request.confirmedDate)]);
    // The hour, where one was booked off the calendar. Stated as its own row
    // rather than folded into the date, because it is the line a concierge reads
    // out to their guest.
    const time = slotTimeRangeLabel({
      startTime: request.confirmedStartTime ?? '',
      endTime: request.confirmedEndTime ?? '',
    });
    if (time) rows.push(['Time', time]);
  } else {
    rows.push([
      request.preferredDates.length > 1 ? 'Preferred dates' : 'Preferred date',
      request.preferredDates.map((d) => formatCalendarDate(d)).join(' · '),
    ]);
  }

  rows.push(['Guests', String(request.guests)]);

  // What was ticked when the request was sent, at the price it was ticked at.
  // Stated on its own rather than left to the price table below, which does not
  // exist until we have quoted the booking — until then this is the partner's only
  // record of having asked for the car.
  if (request.optionalServices?.length) {
    rows.push(['Additional services', optionalServiceLines(request.optionalServices, formatJpy).join(' · ')]);
  }

  if (request.status === 'held') {
    if (request.heldUntil) rows.push(['Answer by', formatDeadlineJst(request.heldUntil)]);
    // Shown before the answer, not only after it, now that the answer can be
    // given here: this is the term the offer commits the partner to, and the offer
    // email and the link page both quote it. Same helper, so all three agree.
    const cancellation = offerCancellationLabel(
      offerCancellation(request, new Date().toISOString()),
      formatDeadlineDayJst,
    );
    if (cancellation) rows.push(['Cancellation terms', cancellation]);
  }

  // Stated on a confirmed booking whether or not there is a window, because
  // "nothing said about cancellation" is read as "cancellable", and on a
  // non-refundable booking that is the expensive way to be wrong.
  if (request.status === 'confirmed') {
    const freeUntil = bookingFreeCancellationPhrase(request, appDeadlineFormats);
    rows.push([
      'Cancellation terms',
      isBookingNonRefundable(request)
        ? 'Not available — this booking is firm'
        : freeUntil
          ? `Until ${freeUntil}`
          : '—',
    ]);
  }

  if (request.status === 'cancelled' && request.cancellationFee) {
    rows.push(['Cancellation fee', formatJpy(request.cancellationFee)]);
  } else if (request.status === 'cancelled' && request.cancellationFeeWaived) {
    // A fee was due and Eighty Days chose not to take it, which is worth a line of
    // its own: silence here reads as a cancellation that never cost anything.
    rows.push(['Cancellation fee', 'Waived']);
  }

  // Why it ended, in the words the model already uses for it. One or the other:
  // a request that never reached 'confirmed' carries `endReason`, one that did was
  // cancelled — and is described by who ended it, never by the free text the desk
  // wrote about it. See `cancellationOutcomeLabel`.
  const outcome = request.status === 'cancelled'
    ? cancellationOutcomeLabel(request)
    : request.endReason
      ? bookingRequestEndReasonLabels[request.endReason]
      : partnerStatusNotes[request.status];

  return (
    <article className='tw:rounded-lg tw:border tw:bg-card tw:p-5'>
      <div className='tw:flex tw:flex-wrap tw:items-start tw:justify-between tw:gap-x-4 tw:gap-y-2'>
        <div className='tw:min-w-0'>
          <Link
            to={`/experiences/${request.experienceId}`}
            className='tw:font-serif tw:text-lg tw:transition-colors tw:hover:text-ed-gold'
          >
            {request.experienceName}
          </Link>
          <p className='tw:mt-0.5 tw:text-xs tw:text-ed-stone'>
            For {request.customerFirstName} {request.customerLastName} · requested {formatDate(request.createdAt)}
          </p>
          {/* What to quote when writing or ringing about this booking. Its own
              line rather than another clause on the one above, and in the same
              place on every card, because it is the thing a concierge copies out
              — not another fact about the request.

              Absent until Eighty Days has given the booking one, which is most
              of the time on the day it is made. Said as nothing rather than as a
              dash: a reference that does not exist yet is not a blank field, and
              there is nothing here for the partner to do about it. */}
          {request.portalRequestCode && (
            <p className='tw:mt-1 tw:text-xs tw:text-ed-stone'>
              <span className='tw:tracking-wider tw:uppercase'>Reference</span>{' '}
              <span className='tw:font-mono tw:text-ed-charcoal'>{request.portalRequestCode}</span>
            </p>
          )}
        </div>
        <span
          className={`tw:shrink-0 tw:rounded-full tw:px-2.5 tw:py-1 tw:text-xs tw:font-medium ${statusPillClass[request.status]}`}
        >
          {partnerStatusLabels[request.status]}
        </span>
      </div>

      <dl className='tw:mt-4 tw:grid tw:gap-x-8 tw:gap-y-2 tw:sm:grid-cols-2'>
        {/* Keyed by position: two services on one booking may share a name. */}
        {rows.map(([label, value], i) => (
          <div key={i} className='tw:flex tw:justify-between tw:gap-4 tw:border-b tw:border-ed-umber/10 tw:pb-2'>
            <dt className='tw:text-[0.65rem] tw:tracking-wider tw:text-ed-stone tw:uppercase'>{label}</dt>
            <dd className='tw:text-right tw:text-sm'>{value}</dd>
          </div>
        ))}
      </dl>

      <PriceTable request={request} />

      {outcome && <p className='tw:mt-3 tw:text-sm tw:text-muted-foreground'>{outcome}</p>}

      {/* What Eighty Days said when they offered the date. Above the partner's own
          special requests, since it is the half of the conversation they have
          not read yet — and kept after the offer is answered, because a note
          about what to bring or when to arrive matters most on the day. */}
      {request.publicNotes && (
        <p className='tw:mt-3 tw:border-l-2 tw:border-ed-gold/40 tw:pl-3 tw:text-sm tw:whitespace-pre-line'>
          <span className='tw:text-ed-stone'>From Eighty Days:</span> {request.publicNotes}
        </p>
      )}

      {request.specialRequests && (
        <p className='tw:mt-3 tw:text-sm tw:text-muted-foreground'>
          <span className='tw:text-ed-stone'>You asked:</span> {request.specialRequests}
        </p>
      )}

      {/* A held date the email never reached. Said rather than left to be
          noticed — they would otherwise wait for a message that is not coming —
          and no longer a dead end: the buttons below are the answer, which is
          the case this page's own answering path exists for. */}
      {request.status === 'held' && request.offerEmailError && (
        <p className='tw:mt-3 tw:flex tw:items-start tw:gap-2 tw:rounded tw:border tw:border-ed-red/30 tw:bg-ed-red/5 tw:px-3 tw:py-2 tw:text-sm tw:text-ed-red'>
          <AlertTriangle className='tw:mt-0.5 tw:size-4 tw:shrink-0' />
          <span>We could not deliver this offer by email. You can still answer it here.</span>
        </p>
      )}

      {/* Answering, and where the other copy of this offer went. The inbox is
          still named rather than implied — the offer goes to whoever submitted
          the request, who may be on a different shift from whoever is reading
          this, and either of them may be the one to answer it. */}
      {request.status === 'held' && (
        <>
          <div className='tw:mt-4 tw:flex tw:flex-col tw:gap-3 tw:sm:flex-row'>
            <Button onClick={() => onAnswer('confirm')} className='tw:sm:flex-1'>
              Confirm this booking
            </Button>
            <Button variant='outline' onClick={() => onAnswer('decline')} className='tw:sm:flex-1'>
              Decline
            </Button>
          </div>
          <p className='tw:mt-3 tw:flex tw:items-start tw:gap-2 tw:text-sm tw:text-muted-foreground'>
            <Mail className='tw:mt-0.5 tw:size-4 tw:shrink-0' />
            <span>
              Also sent to <span className='tw:text-ed-black'>{request.requestedByEmail}</span>, and can be answered
              from there
            </span>
          </p>
        </>
      )}

      {/* A confirmed booking is the partner's to release — the terms it was
          confirmed under say what that costs, and the dialog states the figure
          before anything happens. */}
      {request.status === 'confirmed' && (
        <div className='tw:mt-4 tw:flex tw:justify-end'>
          <button
            type='button'
            onClick={onCancel}
            className='tw:text-xs tw:tracking-wider tw:text-ed-stone tw:uppercase tw:transition-colors tw:hover:text-ed-red'
          >
            Cancel booking
          </button>
        </div>
      )}
    </article>
  );
}
